import React, { useEffect, useState } from 'react';

import { Box, Button, Checkbox, Link, Modal, ModalDialog, Stack, Typography } from '@mui/joy';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import CalyxLogoCircle from 'jsx:../assets/icons/calyx-logo-circle.svg';
import { IoInformationCircleOutline } from 'react-icons/io5';

import { track } from '../services/analytics';
import {
  EVENT_TOU_MODAL,
  EVNT_TOU_modal_accepted,
  PGV_TOU_modal_shown,
} from '../services/analytics/events';
import { makeAuthenticatedPostRequest } from '../services/axios';
import { acceptSuperPollutantsTerms } from '../services/axios/endpoints';
import { queryKeys } from '../services/react-query/queryKeys';

import { Terms } from './Terms';

type Props = {
  open: boolean;
};

const TermsAcceptanceModal: React.FC<Props> = ({ open }) => {
  const [agreed, setAgreed] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const queryClient = useQueryClient();

  const { mutate: acceptTerms, isPending } = useMutation({
    mutationFn: () => makeAuthenticatedPostRequest(acceptSuperPollutantsTerms, {}),
    onSuccess: () => {
      // Optimistically close the modal immediately, then refetch in background
      queryClient.setQueryData(queryKeys.auth.userInfo(), (old: any) => ({
        ...old,
        data: { ...old?.data, show_super_pollutants_terms_modal: false },
      }));
      queryClient.invalidateQueries({ queryKey: queryKeys.auth.userInfo() });
    },
  });

  useEffect(() => {
    if (open) {
      track(PGV_TOU_modal_shown, EVENT_TOU_MODAL);
    }
  }, [open]);

  return (
    <>
      <Modal
        open={open}
        disableEscapeKeyDown
        slotProps={{
          backdrop: {
            sx: {
              backdropFilter: 'blur(6px)',
              backgroundColor: 'rgba(0, 0, 0, 0.4)',
              pointerEvents: 'all', // blocks interaction even if modal dialog is removed from devtools
            },
          },
        }}
      >
        <ModalDialog
          aria-labelledby='terms-acceptance-modal'
          sx={{
            width: { xs: '90vw', sm: 520 },
            maxWidth: 520,
            p: 0,
            overflow: 'hidden',
            gap: 0,
          }}
        >
          <Stack sx={{ p: 4, gap: 3 }}>
            <CalyxLogoCircle width={48} height={48} />

            <Stack>
              <Typography level='h4' fontWeight='xl'>
                Terms of use
              </Typography>
              <Typography level='body-md' sx={{ color: (theme) => theme.palette.text.secondary }}>
                Calyx Global platform
              </Typography>
            </Stack>

            <Stack
              direction='row'
              gap={2}
              sx={{
                background: (theme) => theme.palette.background.body,
                borderRadius: 1,
                p: 2,
              }}
            >
              <Box sx={{ mt: '2px', flexShrink: 0 }}>
                <IoInformationCircleOutline size={32} />
              </Box>
              <Stack>
                <Typography level='title-md' fontWeight='lg'>
                  Important — Please Read Carefully
                </Typography>
                <Typography
                  level='body-sm'
                  fontWeight={'sm'}
                  sx={{ color: (theme) => theme.palette.text.secondary }}
                >
                  Use of the Calyx Global platform, and the data and insights contained therein, is
                  for your <strong>internal business purposes only</strong>. You may not scrape or
                  copy the Calyx Global data, or share it with any third parties, without explicit
                  approval from Calyx Global in writing.
                </Typography>
              </Stack>
            </Stack>

            <Stack direction='row' gap={1} alignItems='flex-start'>
              <Checkbox
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                sx={{ mt: '2px' }}
              />
              <Typography level='body-sm' fontWeight='sm'>
                I agree to the{' '}
                <Link href='https://calyxglobal.com/terms-of-use/' target='_blank'>
                  Terms of Use
                </Link>{' '}
                and will not share Calyx Global data with third parties.
              </Typography>
            </Stack>
          </Stack>

          <Box
            sx={{
              borderTop: (theme) => `1px solid ${theme.palette.divider}`,
              p: 2,
            }}
          >
            <Button
              fullWidth
              disabled={!agreed || isPending}
              loading={isPending}
              onClick={() => {
                track(EVNT_TOU_modal_accepted, EVENT_TOU_MODAL);
                acceptTerms();
              }}
              sx={{ borderRadius: 'sm' }}
            >
              Accept & Continue
            </Button>
          </Box>
        </ModalDialog>
      </Modal>

      <Terms open={showTerms} onClose={() => setShowTerms(false)} />
    </>
  );
};

export { TermsAcceptanceModal };
